import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useProducts } from "../../lib/useProducts";
import { authApi } from "../../lib/auth";
import { errMsg, money } from "../../lib/api";
import { ProductForm } from "./ProductForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogTitle,
} from "../ui/alert-dialog";

export const ProductsManager = () => {
  const { data } = useProducts();
  const qc = useQueryClient();
  const products = Array.isArray(data) ? data : [];
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["products"] });

  const remove = async () => {
    try {
      await authApi.delete(`/admin/products/${toDelete.slug}`);
      toast.success("Item removed from the shop");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div data-testid="admin-products">
      <button data-testid="admin-add-product-btn" onClick={() => { setEditing(null); setOpen(true); }}
        className="press chunk-sm rounded-full bg-[var(--pink)] text-white font-bold px-5 py-3 flex items-center gap-2 mb-6">
        <Plus className="w-5 h-5" /> Add new item
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {products.map((p) => (
          <article key={p.slug} data-testid={`admin-product-${p.slug}`} className="min-w-0 rounded-[22px] chunk-sm bg-white p-3 flex gap-4 items-center">
            <img src={p.image} alt={p.name} className="w-20 h-20 rounded-2xl object-cover border-2 border-[var(--ink)] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-extrabold leading-tight truncate">{p.name}</p>
              <p className="text-sm text-[#5a5470]">{p.category}</p>
              <p className="text-sm font-mono-x">{money(p.price)}{p.courier_fee != null ? ` · courier ${money(p.courier_fee)}` : ""}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button aria-label={`Edit ${p.name}`} data-testid={`admin-edit-${p.slug}`} onClick={() => { setEditing(p); setOpen(true); }}
                className="w-10 h-10 rounded-full border-2 border-[var(--ink)] grid place-items-center bg-[var(--yellow)] hover:bg-[var(--cyan)] transition-colors"><Pencil className="w-4 h-4" /></button>
              <button aria-label={`Delete ${p.name}`} data-testid={`admin-delete-${p.slug}`} onClick={() => setToDelete(p)}
                className="w-10 h-10 rounded-full border-2 border-[var(--ink)] grid place-items-center bg-white hover:bg-[#ffd6e4] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </article>
        ))}
      </div>
      <ProductForm open={open} product={editing} onClose={() => setOpen(false)} onSaved={refresh} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
          <AlertDialogDescription>It will disappear from the shop. Past orders keep their details.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-product-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-product-confirm" onClick={remove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
